import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { GameData, EndGameData } from '@/shared/types';
import type { RootState } from '../index';

interface GamesState {
  byId: { [gameId: number]: GameData };
  allIds: number[];
  endGameData: { [gameId: number]: EndGameData };
  activeGameId: number | null;
}

const initialState: GamesState = {
  byId: {},
  allIds: [],
  endGameData: {},
  activeGameId: null
};

const gamesSlice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    // Game started - add or update game
    gameStarted: (state, action: PayloadAction<GameData>) => {
      const gameId = action.payload.gameId;
      state.byId[gameId] = action.payload;
      if (!state.allIds.includes(gameId)) {
        state.allIds.push(gameId);
      }
      state.activeGameId = gameId;
    },

    // Game ended - store end game data
    gameEnded: (state, action: PayloadAction<EndGameData>) => {
      const gameId = action.payload.gameId;
      state.endGameData[gameId] = action.payload;
    },

    // Clear end game data after processing
    clearEndGameData: (state, action: PayloadAction<number>) => {
      delete state.endGameData[action.payload];
    },

    // Set active game
    setActiveGame: (state, action: PayloadAction<number | null>) => {
      state.activeGameId = action.payload;
    },

    // Clear all games (e.g., on logout)
    clearGames: (state) => {
      state.byId = {};
      state.allIds = [];
      state.endGameData = {};
      state.activeGameId = null;
    }
  }
});

export const {
  gameStarted,
  gameEnded,
  clearEndGameData,
  setActiveGame,
  clearGames
} = gamesSlice.actions;

// Selectors
export const selectAllGames = (state: RootState) =>
  state.games.allIds.map(id => state.games.byId[id]);
export const selectGameById = (state: RootState, gameId: number) =>
  state.games.byId[gameId];
export const selectActiveGameId = (state: RootState) => state.games.activeGameId;
export const selectActiveGame = (state: RootState) =>
  state.games.activeGameId ? state.games.byId[state.games.activeGameId] : null;
export const selectEndGameData = (state: RootState, gameId: number) =>
  state.games.endGameData[gameId];
export const selectAllEndGameData = (state: RootState) => state.games.endGameData;

export default gamesSlice.reducer;
